import { Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useLanguage } from '@/contexts/LanguageContext';
import type { AppLanguage } from '@/i18n';

const logo = require('../../assets/logo.png');

const NAVY = '#0D1B2A';
const GOLD = '#C9A843';
const GOLD_DIM = 'rgba(201,168,67,0.15)';
const GOLD_BORDER = 'rgba(201,168,67,0.35)';
const TEXT_WHITE = '#FFFFFF';
const TEXT_MUTED = 'rgba(255,255,255,0.55)';

const OPTIONS: { lang: AppLanguage; label: string; sublabel: string }[] = [
  { lang: 'el', label: 'Ελληνικά', sublabel: 'Greek' },
  { lang: 'en', label: 'English', sublabel: 'Αγγλικά' },
];

export default function LanguageSelectScreen() {
  const insets = useSafeAreaInsets();
  const { setLanguage } = useLanguage();

  // Μόλις επιλεγεί γλώσσα, το LanguageContext ενημερώνει hasStoredLanguage → true
  // και το useProtectedRoute στο _layout.tsx χειρίζεται αυτόματα την πλοήγηση.
  async function pick(lang: AppLanguage) {
    await setLanguage(lang);
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top + 48, paddingBottom: insets.bottom + 32 }]}>
      <View style={styles.logoWrap}>
        <Image source={logo} style={styles.logo} resizeMode="contain" />
      </View>

      <View style={styles.textWrap}>
        <Text style={styles.title}>Επίλεξε γλώσσα{'\n'}Choose your language</Text>
        <Text style={styles.subtitle}>Μπορείς να την αλλάξεις αργότερα από τις Ρυθμίσεις.{'\n'}You can change this later in Settings.</Text>
      </View>

      <View style={styles.options}>
        {OPTIONS.map(({ lang, label, sublabel }) => (
          <Pressable
            key={lang}
            style={({ pressed }) => [styles.btn, pressed && styles.btnPressed]}
            onPress={() => void pick(lang)}
            accessibilityRole="button"
            accessibilityLabel={label}
          >
            <Text style={styles.btnLabel}>{label}</Text>
            <Text style={styles.btnSub}>{sublabel}</Text>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: NAVY,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    gap: 40,
  },
  logoWrap: {
    alignItems: 'center',
  },
  logo: {
    width: 120,
    height: 120,
  },
  textWrap: {
    alignItems: 'center',
    gap: 12,
  },
  title: {
    color: GOLD,
    fontSize: 22,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 32,
  },
  subtitle: {
    color: TEXT_MUTED,
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 20,
  },
  options: {
    width: '100%',
    gap: 14,
  },
  btn: {
    backgroundColor: GOLD_DIM,
    borderWidth: 1.5,
    borderColor: GOLD_BORDER,
    borderRadius: 14,
    paddingVertical: 18,
    alignItems: 'center',
    gap: 4,
  },
  btnPressed: {
    backgroundColor: 'rgba(201,168,67,0.28)',
  },
  btnLabel: {
    color: TEXT_WHITE,
    fontSize: 18,
    fontWeight: '700',
  },
  btnSub: {
    color: TEXT_MUTED,
    fontSize: 13,
  },
});
